import { useEffect, useRef } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Panel } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { trpc } from '../lib/trpc';

/**
 * Where Trade Me sends the operator back to, with `oauth_token` and `oauth_verifier` on the URL.
 *
 * Hand-parsed rather than through zod: `validateSearch` runs in the eager route tree, and zod there
 * is the bundle cost DESIGN_SYSTEM.md §8 warns about. The server bounds and checks both values.
 */
interface TradeMeCallbackSearch {
  oauth_token?: string;
  oauth_verifier?: string;
}

const text = (value: unknown) =>
  typeof value === 'string' && value.length > 0 && value.length <= 200 ? value : undefined;

export const Route = createFileRoute('/settings/integrations/trademe/callback')({
  validateSearch: (search: Record<string, unknown>): TradeMeCallbackSearch => {
    const token = text(search.oauth_token);
    const verifier = text(search.oauth_verifier);
    return {
      ...(token ? { oauth_token: token } : {}),
      ...(verifier ? { oauth_verifier: verifier } : {}),
    };
  },
  component: TradeMeCallbackPage,
});

function TradeMeCallbackPage() {
  const { oauth_token: oauthToken, oauth_verifier: oauthVerifier } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const complete = useMutation(
    trpc.integrations.tradeMe.complete.mutationOptions({
      meta: { errorTitle: 'Trade Me could not be connected' },
      onSuccess() {
        void queryClient.invalidateQueries({ queryKey: trpc.integrations.list.queryKey() });
        // REPLACE, so Back does not land on this page and try to spend the same approval twice.
        void navigate({
          to: '/settings/integrations',
          search: { connected: 'trademe' },
          replace: true,
        });
      },
    }),
  );

  /*
   * ONCE, however often React mounts this. StrictMode runs effects twice in development, and the
   * server would answer the second with "already used" — correct, but it would replace a success
   * with an error on the screen. The server's single-use claim is the real guard; this is manners.
   */
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current || !oauthToken || !oauthVerifier) return;
    sent.current = true;
    complete.mutate({ oauthToken, oauthVerifier });
  }, [complete, oauthToken, oauthVerifier]);

  // Trade Me sends the operator back without a verifier when they decline, or close the page.
  const declined = !oauthToken || !oauthVerifier;

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar title="Connecting Trade Me" subtitle="Finishing the approval you gave on Trade Me" />

      <Panel className="px-5 py-5">
        {declined ? (
          <p role="alert" className="text-[0.875rem] font-medium">
            Trade Me did not send an approval back, so nothing was connected. If you meant to
            connect, start again.
          </p>
        ) : complete.isError ? (
          // The outcome, which is what this page is for. Why is in the notice.
          <p role="status" className="text-[0.875rem] font-medium">
            Nothing was connected. Start again from Integrations once the reason is dealt with.
          </p>
        ) : (
          <p role="status" className="text-[0.875rem] text-ink-muted">
            Connecting to Trade Me…
          </p>
        )}

        {declined || complete.isError ? (
          <Link
            to="/settings/integrations"
            className="mt-4 inline-block text-[0.875rem] font-semibold text-accent hover:underline"
          >
            Back to Integrations
          </Link>
        ) : null}
      </Panel>
    </div>
  );
}
